import { FontIconName, ToolbarAction } from "client/Components";
import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import {
	RefObject,
	useCallback,
	useMemo,
	useRef
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	registerFollowCategory,
	unregisterFollowCategory
} from "client/Api/Hub/hub";
import { ActionKind } from "client_legacy/ndaw/actions";
import { isNotNil } from "client/Misc/utils";
import { HubEvent, publish } from "../events";

export function useFollowCategoryAction<TContext extends { categoryId: number | null; }>(
	categoryId: number | null,
	isFollowing: boolean,
): ToolbarAction<TContext> {
	const isInteractingWithBackendRef: RefObject<boolean> = useRef<boolean>(false);
	const alertManager: AlertManager = useAlerts();

	const clickHandler = useCallback(
		async (): Promise<void> => {
			try {
				if (categoryId === null) {
					return;
				}

				if (isInteractingWithBackendRef.current) {
					return;
				}

				isInteractingWithBackendRef.current = true;
				if (isFollowing) {
					await unregisterFollowCategory(categoryId);
				} else {
					await registerFollowCategory(categoryId);
				}

				publish(
					HubEvent.CategoryFollowingChanged,
					{
						categoryId,
						isFollowing: !isFollowing
					}
				);

				isInteractingWithBackendRef.current = false;
			} catch (e) {
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				isInteractingWithBackendRef.current = false;
			}
		},
		[
			alertManager,
			categoryId,
			isFollowing
		]
	);

	const { icon, key } = useMemo(
		(): {
			icon: FontIconName;
			key: LocalizationKey;
		} => {
			return isFollowing
				? {
					icon: "check",
					key: "_Following"
				}
				: {
					icon: "plus",
					key: "_Follow"
				};
		},
		[isFollowing]
	);

	const action = useMemo(
		(): ToolbarAction<TContext> => {
			return {
				kind: ActionKind.Regular,
				name: "follow-category",
				title: localize(key),
				icon,
				canExecute: (x): boolean => isNotNil(x.categoryId),
				execute: clickHandler
			};
		},
		[
			clickHandler,
			icon,
			key
		]
	);

	return action;
}